import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, CheckCircle, Lock, AlertCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const setAuth = useAuthStore(s => s.setAuth);

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) { setError('Lien invalide'); setLoading(false); return; }
      try {
        const res = await authAPI.getInviteInfo(token);
        if (res.data.accepted) {
          setError('Cette invitation a deja ete utilisee');
        } else {
          setInviteInfo(res.data);
        }
      } catch (err) {
        setError('Invitation non trouvee ou expiree');
      } finally {
        setLoading(false);
      }
    };
    loadInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authAPI.acceptInvite(token, password);
      setAuth(res.data.token, res.data.user, 'staff');
      toast.success('Compte cree avec succes !');
      navigate('/staff/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la creation du compte');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {error ? (
          <div className="text-center" data-testid="invite-error">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-gray-700 font-medium">{error}</p>
          </div>
        ) : inviteInfo && (
          <>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-violet-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
                Bienvenue {inviteInfo.name} !
              </h1>
              <p className="text-gray-500 text-sm mt-2">
                Vous avez ete invite a rejoindre <strong>{inviteInfo.establishment_name}</strong>
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input value={inviteInfo.email} disabled className="mt-1 bg-gray-50" data-testid="invite-email" />
              </div>
              <div>
                <Label htmlFor="password">Creer un mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-9"
                    placeholder="Min. 6 caracteres"
                    data-testid="invite-password-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="confirm">Confirmer le mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pl-9"
                    placeholder="Confirmer"
                    data-testid="invite-confirm-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#7C3AED] hover:bg-violet-600 text-white rounded-full py-5" data-testid="invite-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Creer mon compte
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
